import {css} from '@mui/material';
import {DlMuiDynamicIcon} from '@wallaby-ui/core';
import * as React from 'react';

import {HaidiHaiTypeFormatter} from './HaidiHaiTypeFormatter';
import {Formatters} from '../light';
import {AG_GRID_COMPATIBILITY} from '../LightTable';
import {HaidiSchema} from 'src/js/common/types';
import {EntityLink} from 'src/js/containers/file';
import {TemplatedCellRendererParams} from 'src/js/containers/grid/grid_types';

type Props = TemplatedCellRendererParams<HaidiSchema.components['schemas']['PatientEventDto']> & {
  sortTimeDirection: 'asc' | 'desc';
  patientId: string | undefined;
  formatters: Formatters;
  snomedLabels: Record<string, string>;
  standardTypeCodeLabel?: string;
  standardTypeTranslation?: string;
};

const TABLE_ROW_PADDING = 6;
const muiStyles = {
  wrapper: css`
    position: relative;
    height: 100%;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    text-align: left;
  `,
  pipeIconButton: css`
    position: absolute;
    transform: translateX(-100%);
    padding: 0;
    color: var(--dlTheme-300);
    width: 9px;

    &[data-pipe='top-bottom'] {
      left: 0;
      top: -${TABLE_ROW_PADDING}px;
      height: calc(100% + ${TABLE_ROW_PADDING * 2 + 1}px);
    }

    &[data-pipe='top'] {
      left: 0;
      top: -${TABLE_ROW_PADDING}px;
      height: calc(50% + ${TABLE_ROW_PADDING}px);
    }

    &[data-pipe='bottom'] {
      left: 0;
      top: calc(50%);
      height: calc(50% + ${TABLE_ROW_PADDING}px);
    }

    &[data-pipe='middle'] {
      left: 0;
      top: calc(50%);
      height: 2px;
    }

    & svg {
      width: 9px;
      height: 100%;
    }
  `,
  eventTypeIconButton: css`
    padding: 0 8px;
    color: var(--dlTheme-700);
  `,
  eventTypeIcon: css`
    width: 16px;
    height: 16px;
  `,
};

export const HealthcareTableEventLinkFormatter: React.FC<Props> = (props) => {
  const {data, sortTimeDirection, patientId: _, formatters, snomedLabels, standardTypeCodeLabel, standardTypeTranslation} = props;

  const {t, tEnum} = formatters;

  type PipeIcon = {
    vertical?: 'top-bottom' | 'top' | 'bottom';
    middle?: boolean;
  };
  let pipeIcon: PipeIcon = {};
  const TOP_PIPE_ICON: PipeIcon = {vertical: 'top', middle: true};
  const BOTTOM_PIPE_ICON: PipeIcon = {vertical: 'bottom', middle: true};
  switch (data.eventType) {
    case 'HOSPITALIZATION_START':
      pipeIcon = sortTimeDirection === 'desc' ? TOP_PIPE_ICON : BOTTOM_PIPE_ICON;
      break;
    case 'HOSPITALIZATION_END':
      pipeIcon = sortTimeDirection === 'desc' ? BOTTOM_PIPE_ICON : TOP_PIPE_ICON;
      break;
    default:
      if (data.isWithinHospitalization) {
        pipeIcon = {vertical: 'top-bottom'};
      }
      break;
  }

  type EventIcon = {
    name: string;
    viewBox: string;
    title: string;
  };
  let eventIcon: EventIcon = {
    name: '',
    viewBox: '',
    title: '',
  };
  let eventTypeString: string = data.eventType;
  let eventTypeChip: JSX.Element | undefined;

  function getHaiChip() {
    const standardTypeData = {type: data.haiType, standardTypeCodeLabel, standardTypeTranslation};
    return <HaidiHaiTypeFormatter {...AG_GRID_COMPATIBILITY} data={standardTypeData} value={data.haiType} style={{marginLeft: 8}} />;
  }

  switch (data.eventType) {
    case 'HOSPITALIZATION_START':
      eventIcon = {
        name: 'haidiBedIcon',
        viewBox: '0 0 12 10',
        title: t('patient.detail.healthcare.eventType.hospitalization_start'),
      };
      eventTypeString = `${t('patient.detail.healthcare.eventType.hospitalization_start')} - ${data.organizationName}`;
      break;
    case 'TRANSFER':
      eventIcon = {
        name: 'haidiTransferIcon',
        viewBox: '0 0 12 13',
        title: t('patient.detail.healthcare.eventType.transfer'),
      };
      eventTypeString = `${t('patient.detail.healthcare.eventType.transfer')} - ${data.organizationName}`;
      break;
    case 'HOSPITALIZATION_END':
      eventIcon = {
        name: 'haidiBedIcon',
        viewBox: '0 0 12 10',
        title: t('patient.detail.healthcare.eventType.hospitalization_end'),
      };
      eventTypeString = t('patient.detail.healthcare.eventType.hospitalization_end');
      break;
    case 'LABORATORY': {
      eventIcon = {
        name: 'haidiLabIcon',
        viewBox: '0 0 12 13',
        title: t('patient.detail.healthcare.eventType.laboratory'),
      };
      const specimen = data.specimen ?? tEnum('LabReportSpecimenTypeEnum', 'UNKNOWN');
      const snomedCode = data.findingSnomedCode
        ? snomedLabels[data.findingSnomedCode] || data.findingSnomedCode
        : tEnum('LabResultInterpretationCodeEnum', data.resultCode).replace(/^./, (char) => char.toUpperCase());
      eventTypeString = `${snomedCode} - ${specimen}`;
      break;
    }
    case 'OUTPATIENT':
      eventIcon = {
        name: 'haidiStethoscopeIcon',
        viewBox: '0 0 12 12',
        title: t('patient.detail.healthcare.eventType.outpatient'),
      };
      eventTypeString = `${t('patient.detail.healthcare.eventType.outpatient.message')} - ${data.organizationName}`;
      break;
    case 'REPORTED_HAI':
      eventIcon = {
        name: 'haidiHaiIcon',
        viewBox: '0 0 13 14',
        title: t('patient.detail.healthcare.eventType.reported_hai'),
        // color: HealthcareTableIconColors.ReportedHai;
      };
      eventTypeString = t('patient.detail.healthcare.eventType.reported_hai');
      eventTypeChip = getHaiChip();
      break;
    case 'CONFIRMED_HAI':
      eventIcon = {
        name: 'haidiHaiIcon',
        viewBox: '0 0 13 14',
        title: t('patient.detail.healthcare.eventType.confirmed_hai'),
        // color: HealthcareTableIconColors.ConfirmedHai;
      };
      eventTypeString = t('patient.detail.healthcare.eventType.confirmed_hai');
      eventTypeChip = getHaiChip();
      break;
    case 'COLONIZATION_HAI':
      eventIcon = {
        name: 'haidiHaiIcon',
        viewBox: '0 0 13 14',
        title: t('patient.detail.healthcare.eventType.colonization_hai'),
        // color: HealthcareTableIconColors.ColonizationHai;
      };
      eventTypeString = t('patient.detail.healthcare.eventType.colonization_hai');
      eventTypeChip = getHaiChip();
      break;
    case 'WORK_IN_PROGRESS_HAI':
      eventIcon = {
        name: 'haidiHaiIcon',
        viewBox: '0 0 13 14',
        title: t('patient.detail.healthcare.eventType.work_in_progress_hai'),
        // color: HealthcareTableIconColors.WorkInProgressHai;
      };
      eventTypeString = t('patient.detail.healthcare.eventType.work_in_progress_hai');
      eventTypeChip = getHaiChip();
      break;
  }

  let eventTypeRoute = '';
  if (data.encounterId && data.documentVersionId) {
    eventTypeRoute = `/encounter/${data.encounterId}/document/${data.documentVersionId}`;
  } else if (data.encounterId) {
    eventTypeRoute = `/encounter/${data.encounterId}`;
  } else if (data.documentVersionId) {
    eventTypeRoute = `/document/${data.documentVersionId}`;
  }
  return (
    <span css={muiStyles.wrapper}>
      {pipeIcon.vertical && (
        <DlMuiDynamicIcon
          iconName='haidiPipeVerticalIcon'
          useFixedViewBox='0 0 9 40'
          inputCss={muiStyles.pipeIconButton}
          data-pipe={pipeIcon.vertical}
          preserveAspectRatio='none'
        />
      )}
      {pipeIcon.middle && (
        <DlMuiDynamicIcon iconName='haidiPipeMiddleIcon' useFixedViewBox='0 0 9 2' inputCss={muiStyles.pipeIconButton} data-pipe='middle' />
      )}
      <DlMuiDynamicIcon
        iconName={eventIcon.name}
        useFixedViewBox={eventIcon.viewBox}
        iconTitle={eventIcon.title}
        inputCss={muiStyles.eventTypeIconButton}
        svgIconInputCss={muiStyles.eventTypeIcon}
      />
      <EntityLink route={eventTypeRoute} titleText={eventTypeString} searchParams={{}} suppressPadding textAlign='left'>
        {eventTypeString}
      </EntityLink>
      {eventTypeChip}
    </span>
  );
};
HealthcareTableEventLinkFormatter.displayName = 'HealthcareTableEventLinkFormatter';
